<template>
  <div class="header">
    <div class="left-header">
      <div class="logo">
        <a href class="toHome">
          <img
            class="img.cmpGNBlogo-org"
            :src="
              require(`@/assets/img/scmp/customerLogo/${customerType}/BI_SCP_CloudMonitoring_light.svg`)
            "
          />
        </a>
      </div>
      <div class="account-info ellipsis" v-lego-tooltip:ellipsis="accountInfo">
        {{ accountInfo }}
      </div>
    </div>

    <div class="utilmenu" v-click-outside="hideAllUtilmenu">
      <sc-icon
        name="ri-custom"
        class="header-icon"
        v-lego-tooltip="{ value: $t('header.customDashboard'), position: 'left' }"
        @click="linkToCustomDashboard"
      />

      <sc-icon
        name="ri-help"
        class="header-icon"
        v-lego-tooltip="{ value: $t('header.support'), moreAction: 'clickToHide' }"
        @click="toggleUtilmenuUserguide"
      />

      <div class="selectmenu_group">
        <lego-dropdown
          class="region_selectmenu"
          autoSearch
          groupSort
          :hasScroll="8"
          :items="regions"
          :modelValue="region"
          :groups="groups"
          width="160px"
          :disabled="isRoleId"
          @update:modelValue="redirectURLWithRegion"
          @click="hideAllUtilmenu()"
        />
      </div>

      <sc-icon
        name="ri-user-header"
        v-lego-tooltip="{
          value: $t('header.myMenu'),
          position: 'right',
          moreAction: 'clickToHide'
        }"
        class="header-icon"
        @click="toggleUtilmenuAccount"
      />
      <!-- dropdown layers -->
      <div class="popup-utilmenu userguide" :class="isUserGuide ? 'opened' : ''">
        <!-- 사용자 가이드 임시 하드 코딩 -->
        <div class="utilmenu-group">
          <div class="documentation-url support-option">
            <a target="_blank" :href="documentationUrl" style="text-decoration: none">{{
              t('label.documentation')
            }}</a>
          </div>
        </div>
      </div>
      <div class="popup-utilmenu account" :class="isAccountInfo ? 'opened' : ''">
        <div>
          <div class="user-email" v-lego-tooltip:ellipsis="userEmail">{{ userEmail }}</div>
          <div class="user-information__header__detail">
            <div>{{ timeZoneInfo }} {{ timezoneUtcDstOffset }}</div>
          </div>
        </div>
        <div class="logout-wrap">
          <div class="logout-button" @click="logout()">{{ $t('header.logout') }}</div>
        </div>
      </div>
      <!-- // dropdown layers -->
    </div>
    <Loading />
  </div>
</template>
<script setup>
import cmpLogin from '@/utils/cmpLogin';
import { redirectURLWithRegion, getRegionFromDomain } from '@/utils/cpmRegion';
import restBase from '@/plugin/restBase';

import {
  defineAsyncComponent,
  ref,
  computed,
  onMounted,
  getCurrentInstance,
  onBeforeUnmount
} from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import moment from 'moment';
import legoEventBus from '@/lego-component/utils/eventBus/eventBus';

const { t, locale } = useI18n();
const { $http, $modal, $const, $util } = getCurrentInstance().appContext.config.globalProperties;

const Loading = defineAsyncComponent(() => import('./Loading.vue'));

const isCustomDashboard = ref(false);
const isUserGuide = ref(false);
const isAccountInfo = ref(false);

const groups = ref([{ all: true }]);
const accountInfo = ref('');

const sessionEmail = $http.getRoleId()
  ? window.sessionStorage.getItem('roleName') + '@' + window.sessionStorage.getItem('projectId')
  : window.sessionStorage.getItem('userEmail');
const userEmail = sessionEmail || 'gilding.hong@samsung.com';
const roleId = ref();
roleId.value = restBase.getRoleId();

const $store = useStore();
const $router = useRouter();

const CUSTOMER_TYPE = {
  PP: 'pp',
  PS: 'ps',
  PG: 'pg'
};

const domainGuide = process.env.VUE_APP_DOMAIN_GUIDE;

let customerType = process.env.VUE_APP_CUSTOMER_TYPE;
if (!Object.values(CUSTOMER_TYPE).includes(customerType)) {
  customerType = CUSTOMER_TYPE.PP;
}

const documentationUrl = computed(() => {
  if (locale.value === 'ko') return domainGuide + $const.DOCUMENTATION_GUIDE;
  return `${domainGuide + locale.value}/${$const.DOCUMENTATION_GUIDE}`;
});

const isRoleId = computed(() => !!roleId.value);

const timeZoneInfo = computed(() => {
  return sessionStorage.getItem('timeZoneInfo') || '';
});

const timezoneUtcDstOffset = computed(() => {
  if (window.sessionStorage.length) {
    const { timeZoneInfo } = window.sessionStorage;
    const offset = timeZoneInfo ? `(GMT ${moment().tz(timeZoneInfo).format('Z')})` : '';
    return offset;
  }
  return '';
});

const region = computed(() => $store.state.region.value);

const regions = computed({
  get() {
    return $store.state.regions;
  },
  set(regions) {
    $store.commit('setRegions', regions);
  }
});

onMounted(() => {
  getRegionList();
  addHandler();
  if (window.opener) {
    window.opener.postMessage('service load message', '*');
  }
  legoEventBus.$on('close_tooltips', () => hideAllUtilmenu());
  getAccountInfo();
});

onBeforeUnmount(() => {
  removeHandler();
});

const getRegionList = () => {
  const language = localStorage.getItem('language');
  const useIDP = process.env.VUE_APP_USE_IDP;
  let apiUrl = '';
  if (useIDP === 'false') {
    apiUrl = process.env.VUE_APP_PROXY;
  }
  apiUrl += '/console/api/iam/v1/regions';
  $http.get(apiUrl, {}, false, false, { headers: { 'Scp-Language': $util.getLocateString(language) } }).then((data) => {
    if (data && data.regions && data.regions.length) {
      regions.value = [];
      data.regions.forEach((e) => {
        regions.value.push({
          value: e.id,
          text: e.display_name
        });
      });
      // Logic for check current region in domain, if it has region, set region in domain to state
      const currentRegion = getRegionFromDomain();
      const checkRegion = regions.value.find((region) => region.value === currentRegion);
      if (checkRegion) $store.commit('setRegion', checkRegion);
      else $store.commit('setRegion', regions.value[0]);
    }
  });
};

const getAccountInfo = () => {
  const accountId = $util.getAccountId();
  const accountUrl = `${process.env.VUE_APP_PROXY}/console/api/iam/v1/accounts/${accountId}`;
  $http
    .get(accountUrl, {}, true, false, { headers: { 'Scp-accountid': accountId } })
    .then((data) => {
      accountInfo.value = data.account.name;
      if (data.account.alias) {
        accountInfo.value += ` (${data.account.alias})`;
      }
    });
};

function toggleUtilmenuDashboard() {
  isCustomDashboard.value = !isCustomDashboard.value;
  isUserGuide.value = false;
  isAccountInfo.value = false;
}

function toggleUtilmenuUserguide() {
  isCustomDashboard.value = false;
  isUserGuide.value = !isUserGuide.value;
  isAccountInfo.value = false;
}

function toggleUtilmenuAccount() {
  isCustomDashboard.value = false;
  isUserGuide.value = false;
  isAccountInfo.value = !isAccountInfo.value;
}

function hideAllUtilmenu() {
  isCustomDashboard.value = false;
  isUserGuide.value = false;
  isAccountInfo.value = false;
}

function linkToCustomDashboard() {
  toggleUtilmenuDashboard();
  if ($router.currentRoute.value.path !== '/customDashboard') {
    $router.push({ path: '/customDashboard' });
  }
}

function logout() {
  $modal.open({
    rootId: 'app',
    type: 'confirm',
    title: t('title.notice'),
    message: t('message.logout'),
    action: () => {
      cmpLogin.logout();
    },
    cancel: () => {}
  });
}

function addHandler() {
  window.addEventListener('message', receiveMessage, false);
}

function removeHandler() {
  window.removeEventListener('message', receiveMessage, false);
}

function receiveMessage(event) {
  if (typeof event.data == 'string' && event.data.includes('PROJECT')) {
    project.value = event.data;
  }
}
</script>

<style lang="scss" scoped>
$selectedmenu-list: project_selectmenu region_selectmenu;
.header {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
  height: 48px;
  width: 100%;
  //padding: 12px 32px 12px 24px;
  background-color: var(--bg-header);
  display: flex;
  justify-content: space-between;
  align-items: center;

  .left-header {
    min-width: 814px;
    height: 32px;
    margin: 12px 0 12px 24px;
    display: flex;
    gap: 40px;
    align-items: center;

    :deep .hasScroll {
      width: 203px;
    }
  }

  .selectmenu_group {
    display: flex;
    align-items: center;
    width: 160px;
    height: 20px;
    gap: 8px;
    position: relative;

    &::before,
    &::after {
      content: '';
      width: 1px;
      height: 10px;
      position: absolute;
      top: 50%;
      transform: translate(0, -50%);
      background-color: var(--color-header);
      opacity: 0.4;
    }

    &::before {
      left: 0;
    }

    &::after {
      right: 0;
    }

    :deep(.lego-dropdown--open) {
      .opus-icon__arrow_down {
        opacity: 1 !important;
      }
    }
  }

  .selectmenu_group-label {
    color: var(--color-header);
    font-weight: 500;
  }

  @each $item in $selectedmenu-list {
    .#{$item} {
      user-select: none;
      width: 160px !important;
      height: 20px !important;
      padding: 0 15px;

      :deep(.lego-dropdown__title) {
        border-radius: 100px;
        border: none !important;
        padding: 0;
        background-color: transparent;
        color: var(--color-header);
        font-weight: 500;

        &:hover,
        &:focus {
          border-color: #2b303b;
        }
        .lego-dropdown__selected span {
          display: block;
          width: 100%;
          line-height: 20px;
        }
      }
      &:hover {
        :deep(.opus-icon__arrow_down) {
          opacity: 1;
        }
      }

      :deep(.opus-icon__arrow_down) {
        color: var(--color-header);
        opacity: 0.5;
      }

      :deep(.lego-dropdown-menu) {
        margin-top: 4px !important;
        left: 0;

        .auto-complete__search-box {
          .lego-text-field {
            .lego-text-field__input {
              padding-right: 32px;
            }

            .lego-text-field__input--with-icon-one {
              padding-right: 45px !important;
            }
          }
        }
      }
    }
  }

  :deep .c-scroll {
    max-height: 277px !important;
  }

  .toHome {
    display: flex;
    align-items: center;
  }

  img.cmpGNBlogo-org {
    width: 284px;
    height: 19px;
    object-fit: contain;
  }

  .utilmenu {
    position: absolute;
    top: 12px;
    right: 24px;
    display: flex;
    gap: 20px;
    align-items: center;
    align-content: center;

    .user-email {
      text-overflow: ellipsis;
      max-width: 212px;
      white-space: nowrap;
      overflow: hidden;
    }

    span {
      display: inline-block;
      width: 24px;
      height: 24px;
      margin: 0 0 0 20px;
      background-color: transparent;
      background-repeat: no-repeat;
      background-position: 0 0;
      object-fit: contain;
      opacity: 0.8;
      cursor: pointer;

      em {
        display: inline-block;
        text-indent: -9999px;
      }
    }

    .header-icon {
      opacity: 0.7;
      cursor: pointer;
      color: var(--color-header);
      &:hover {
        opacity: 1;
      }
      :deep(path) {
        fill: currentColor;
      }
    }
  }
}

.not-used {
  cursor: default !important;
}

.popup-utilmenu {
  display: none;
  position: fixed;
  top: 48px;
  right: 103px;
  width: 158px;
  height: auto;
  padding: 8px;
  background-color: #fff;
  box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.2);
  border: solid 1px var(--silver);
  z-index: 1;

  &:after {
    content: '';
    position: absolute;
    top: -15px;
    right: 14px;
    width: 0;
    height: 0;
    border: 9px solid transparent;
    border-bottom: 9px solid #fff;
  }

  &.opened {
    display: block;
  }

  &.dashboard {
    right: 62px;
  }

  &.userguide {
    right: 235px;
  }

  &.account {
    width: 268px;
    right: 11px;
    padding: 16px 20px 8px 20px;

    > div:first-child {
      display: block;
      align-items: center;
      padding-bottom: 20px;
      border-bottom: 1px solid var(--color-ui-ui09);

      .user-email {
        padding-bottom: 8px;
        font-weight: 500;
      }

      .user-information__header__detail {
        color: #737a8c;
        font-size: 12px;
      }
    }

    .user-icon {
      background-color: transparent;
      margin-right: 8px !important;
    }

    > div:nth-child(2) {
      li {
        position: relative;
        display: flex;
        cursor: pointer;
        height: 36px;
        padding: 16px 0 4px 0;
        font-size: 14px;
        font-weight: 400;
        line-height: 21px;

        &:hover {
          background-color: rgba(54, 79, 199, 0.04);
        }
      }
    }
  }

  .utilmenu-group {
    display: block;
    user-select: none;
    cursor: pointer;

    .support-option {
      padding: 8px;

      &:hover {
        background-color: #eef2fc;
      }
    }

    .guide-url {
      padding-bottom: 12px;
    }
  }

  .logout-wrap {
    height: 40px;
    padding-top: 8px;

    .logout-button {
      padding-left: 8px;
      width: 244px;
      height: 32px;
      margin-left: -8px;
      display: flex;
      align-items: center;
      cursor: pointer;

      &:hover {
        background-color: #eef2fc;
      }
    }
  }
}

a {
  color: #000;
}
:deep(.lego-dropdown__group) {
  margin-top: 0 !important;
}
.account-info {
  padding: 6px 20px;
  min-width: 240px;
  height: 32px;
  // background: linear-gradient(270deg, #ebf1ff 0%, #dcd7ff 58%);
  background: linear-gradient(90deg, #dcd7ff 0%, #ebf1ff 100%);
  border-radius: 999px;
  font-size: 14px;
  font-weight: 500;
  color: #5135ff;
}
</style>
